import { ParameterRegistry } from '../core/parameterRegistry';

// PixelMapPanel: renders a square pixel map of parameters with simple color mapping.

export class PixelMapPanel {
	private canvas: HTMLCanvasElement | null = null;
	private ctx: CanvasRenderingContext2D | null = null;
	private imageData: ImageData | null = null;
	private buffer: Uint8ClampedArray | null = null;
	private devicePixelRatio: number = Math.max(1, (window as any).devicePixelRatio || 1);
	private tooltip: HTMLDivElement | null = null;
	private lastDrawAt: number = 0;
	private minFrameMs: number = 33; // ~30 FPS throttle

	// UI placement
	private readonly widthPx: number;
	private readonly pixelSize: number; // CSS pixel size per parameter pixel
	private readonly padding: number = 8;

	constructor(widthPx: number = 240, pixelSize: number = 6) {
		this.widthPx = widthPx;
		this.pixelSize = pixelSize;
	}

	private ensureCanvas(): void {
		if (this.canvas) return;
        const c = document.createElement('canvas');
        c.id = 'parameter-pixel-map';
        // Prefer mounting inside the equilibrium bottom panel so it fully utilizes that area
        const panel = document.getElementById('equilibrium-layout-panel');
        if (panel) {
            // Position just above the bottom panel so it never covers the equilibrium layout
            const rect = panel.getBoundingClientRect();
            (this as any)._mountedInPanel = false;
            c.style.position = 'fixed';
            c.style.left = '16px';
            c.style.bottom = `${Math.max(0, Math.floor(rect.height)) + 16}px`;
            c.style.zIndex = '10005';
            // Re-enable hover for stats tooltip
            c.style.pointerEvents = 'auto';
            c.style.background = 'transparent';
            c.style.border = '1px solid rgba(0,0,0,0.18)';
            c.style.borderRadius = '6px';
            c.style.backdropFilter = 'none';
            document.body.appendChild(c);
        } else {
            // Fallback placement when panel not present
            (this as any)._mountedInPanel = false;
            c.style.position = 'fixed';
            c.style.left = '16px';
            c.style.bottom = '16px';
            c.style.zIndex = '10005';
            c.style.pointerEvents = 'auto';
            c.style.background = 'transparent';
            c.style.border = '1px solid rgba(0,0,0,0.18)';
            c.style.borderRadius = '6px';
            c.style.backdropFilter = 'none';
            document.body.appendChild(c);
        }
		this.canvas = c;
		this.ctx = c.getContext('2d');

		// Tooltip setup
		const tip = document.createElement('div');
		tip.id = 'parameter-pixel-map-tooltip';
		tip.style.position = 'fixed';
		tip.style.pointerEvents = 'none';
		tip.style.zIndex = '10007';
		tip.style.background = 'rgba(17,17,17,0.92)';
		tip.style.color = '#fff';
		tip.style.padding = '6px 8px';
		tip.style.borderRadius = '6px';
		tip.style.font = '12px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
		tip.style.boxShadow = '0 2px 10px rgba(0,0,0,0.25)';
		tip.style.display = 'none';
		document.body.appendChild(tip);
		this.tooltip = tip;

		c.addEventListener('mousemove', (ev) => this.onMouseMove(ev));
		c.addEventListener('mouseleave', () => this.hideTooltip());
	}

	private ensureBuffer(side: number): void {
		if (!this.canvas) return;
		// Match the equilibrium layout panel size when available
		let targetW = this.widthPx;
		let targetH = side * this.pixelSize + this.padding * 2;
        const eq = document.getElementById('equilibrium-layout-panel') as HTMLElement | null;
        const mountedInPanel = (this as any)._mountedInPanel === true;
        if (eq && mountedInPanel) {
            const rect = eq.getBoundingClientRect();
            targetW = Math.max(1, Math.floor(rect.width - this.padding * 2));
            targetH = Math.max(1, Math.floor(rect.height - this.padding * 2));
            this.canvas.style.width = `${targetW}px`;
            this.canvas.style.height = `${targetH}px`;
        } else {
			const cssSize = side * this.pixelSize + this.padding * 2;
			targetW = Math.max(this.widthPx, cssSize);
			targetH = cssSize;
			this.canvas.style.width = `${targetW}px`;
			this.canvas.style.height = `${targetH}px`;
		}
		const dpr = this.devicePixelRatio;
		if (this.canvas.width !== Math.floor(targetW * dpr) || this.canvas.height !== Math.floor(targetH * dpr)) {
			this.canvas.width = Math.floor(targetW * dpr);
			this.canvas.height = Math.floor(targetH * dpr);
			this.imageData = null;
			this.buffer = null;
		}
		if (!this.ctx) return;
		if (!this.imageData || !this.buffer) {
			this.imageData = this.ctx.createImageData(this.canvas.width, this.canvas.height);
			this.buffer = this.imageData.data;
		}
	}

	private hslToRgb(h: number, s: number, l: number): [number, number, number] {
		const c = (1 - Math.abs(2 * l - 1)) * s;
		const hp = h / 60;
		const x = c * (1 - Math.abs((hp % 2) - 1));
		let r = 0, g = 0, b = 0;
		if (hp >= 0 && hp < 1) { r = c; g = x; b = 0; }
		else if (hp < 2) { r = x; g = c; b = 0; }
		else if (hp < 3) { r = 0; g = c; b = x; }
		else if (hp < 4) { r = 0; g = x; b = c; }
		else if (hp < 5) { r = x; g = 0; b = c; }
		else { r = c; g = 0; b = x; }
		const m = l - c / 2;
		return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
	}

	private colorFor(desc: any, normalize: (id: string, raw: number) => number, now: number): [number, number, number] {
		if (desc.type === 'numeric') {
			const t = normalize(desc.id, desc.value as number);
			// Hue map 240 (blue) to 0 (red)
			const hue = (1 - t) * 240;
			let sat = 0.75, light = 0.55;
			// brief luminance pulse on change
			const changedAgo = desc.lastChangeAt ? (now - desc.lastChangeAt) : 1e9;
			if (changedAgo < 300) light = 0.7;
			return this.hslToRgb(hue, sat, light);
		}
		// categorical palettes (basic set)
		const key = String(desc.value).toLowerCase();
		switch (key) {
			case 'fire': return [235, 94, 52];
			case 'earth': return [152, 118, 84];
			case 'air': return [100, 190, 255];
			case 'water': return [72, 123, 237];
			default: {
				// hash to hue for other categories (e.g., zodiac names)
				let hash = 0;
				for (let i = 0; i < key.length; i++) hash = ((hash << 5) - hash) + key.charCodeAt(i);
				hash = Math.abs(hash);
				const hue = hash % 360;
				return this.hslToRgb(hue, 0.65, 0.55);
			}
		}
	}

	private showTooltip(x: number, y: number, desc: any): void {
		if (!this.tooltip) return;
		const valueStr = desc.type === 'numeric' ? String((desc.value as number).toFixed ? (desc.value as number).toFixed(3) : desc.value) : String(desc.value);
		this.tooltip.innerHTML = `<b>${desc.name}</b><br><span style="opacity:0.85;">${desc.category}</span><br>${valueStr}`;
		this.tooltip.style.display = 'block';
		// Position tooltip away from cursor; prefer above-right, then flip to stay onscreen
		const margin = 16;
		const tipRect = this.tooltip.getBoundingClientRect();
		let tx = x + margin;
		let ty = y - tipRect.height - margin; // try above cursor
		if (ty < 8) ty = y + margin; // if too high, place below
		if (tx + tipRect.width > window.innerWidth - 8) tx = x - tipRect.width - margin; // flip to left if overflow
		if (tx < 8) tx = 8;
		if (ty + tipRect.height > window.innerHeight - 8) ty = Math.max(8, window.innerHeight - tipRect.height - 8);
		this.tooltip.style.left = `${tx}px`;
		this.tooltip.style.top = `${ty}px`;
	}

	private hideTooltip(): void {
		if (this.tooltip) this.tooltip.style.display = 'none';
	}

	private onMouseMove(ev: MouseEvent): void {
		if (!this.canvas) return;
		// Determine which parameter index the mouse is over
		const rect = this.canvas.getBoundingClientRect();
		const x = ev.clientX - rect.left;
		const y = ev.clientY - rect.top;
		// Compute using CSS pixels
		const sidePx = (this as any)._pxCss || this.pixelSize;
		const padPx = this.padding;
		const side = Math.max(0, Math.floor((this.canvas.height / this.devicePixelRatio - padPx * 2) / sidePx));
		if (side <= 0) { this.hideTooltip(); return; }
		const gridX = Math.floor((x - padPx) / sidePx);
		const gridY = Math.floor((y - padPx) / sidePx);
		if (gridX < 0 || gridY < 0 || gridX >= side || gridY >= side) { this.hideTooltip(); return; }
		const idx = gridY * side + gridX;
		// Ask registry for parameters via last render cache
		const params = (this as any)._lastParams as any[] | undefined;
		if (!params || idx < 0 || idx >= params.length) { this.hideTooltip(); return; }
		this.showTooltip(ev.clientX, ev.clientY, params[idx]);
	}

	draw(registry: ParameterRegistry): void {
		const nowTime = performance.now();
		if (nowTime - this.lastDrawAt < this.minFrameMs) return;
		this.lastDrawAt = nowTime;
		this.ensureCanvas();
		if (!this.canvas || !this.ctx) return;
		const side = registry.getGridSide();
		this.ensureBuffer(side);
		if (!this.imageData || !this.buffer) return;

		// Clear
		for (let i = 0; i < this.buffer.length; i++) this.buffer[i] = 0;

		const params = registry.getParameters();
		(this as any)._lastParams = params;
		const dpr = this.devicePixelRatio;
		// Dynamically size pixels to fill the panel like the equilibrium layout
		const cssW = this.canvas.style.width ? parseFloat(this.canvas.style.width) : this.canvas.width / dpr;
		const cssH = this.canvas.style.height ? parseFloat(this.canvas.style.height) : this.canvas.height / dpr;
		const pxCss = Math.max(1, Math.floor(Math.min((cssW - this.padding * 2) / (side || 1), (cssH - this.padding * 2) / (side || 1))));
		(this as any)._pxCss = pxCss;
		const px = pxCss * dpr;
		const pad = this.padding * dpr;
		const now = performance.now();

		for (let idx = 0; idx < params.length; idx++) {
			const row = Math.floor(idx / side);
			const col = idx % side;
			const x0 = Math.floor(pad + col * px);
			const y0 = Math.floor(pad + row * px);
			const [r, g, b] = this.colorFor(params[idx], (id, v) => registry.getNormalized(id, v), now);
			for (let y = 0; y < px; y++) {
				for (let x = 0; x < px; x++) {
					const cx = x0 + x;
					const cy = y0 + y;
					const off = (cy * this.canvas.width + cx) * 4;
					this.buffer[off + 0] = r;
					this.buffer[off + 1] = g;
					this.buffer[off + 2] = b;
					this.buffer[off + 3] = 255;
				}
			}
		}

		this.ctx.putImageData(this.imageData, 0, 0);
	}
}


